import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useCostDetail, useCosts } from './hooks/useCosts';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { CostFormModal } from './components/CostFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-600',
  verified: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  rejected: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  pending: 'Chờ xử lý',
  verified: 'Đã xác minh',
  approved: 'Đã phê duyệt',
  rejected: 'Từ chối',
};

const typeColors: Record<string, string> = {
  labor: 'bg-slate-100 text-slate-500',
  material: 'bg-emerald-100 text-emerald-600',
  equipment: 'bg-blue-100 text-blue-600',
  other: 'bg-indigo-100 text-indigo-600',
};

export default function CostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: item, isLoading } = useCostDetail(id);
  const { approveCost, isApproving } = useCosts(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết chi phí...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin chi phí</div>;

  const handleApprove = async () => {
    try {
      await approveCost({ id: id!, approverId: profile?.id || '' });
      toast.success('Đã phê duyệt khoản chi phí');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const formatCurrency = (val: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'work', label: 'Công việc liên quan' },
    { value: 'media', label: 'Chứng từ / Ảnh' },
    { value: 'history', label: 'Lịch sử phê duyệt' },
  ];

  return (
    <PageDetailLayout
      title={`${item.category}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.reference_code || item.id.slice(-6).toUpperCase()}</span>
          <span className="text-slate-300">|</span>
          <Badge className={`${typeColors[item.cost_type]} border-transparent text-[10px] font-bold `}>
            {item.cost_type}
          </Badge>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {new Date(item.cost_date).toLocaleDateString('vi-VN')}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Theo dõi chi phí', path: '/maintenance/cost-tracking' },
        { label: item.category }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code !== 'approved' && (
            <AuthButton module="maintenance" action="approve" variant="success" icon="ri-check-line" onClick={handleApprove} isLoading={isApproving}>
              Phê duyệt chi phí
            </AuthButton>
          )}
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Tổng thanh toán" value={formatCurrency(item.total_amount)} icon="ri-money-dollar-circle-line" variant="primary" />
                <KPICard title="Số lượng" value={item.quantity} icon="ri-apps-2-line" variant="info" suffix={` ${item.unit_price.toLocaleString()} / đơn vị`} />
                <KPICard title="Định mức" value="Trong hạn" icon="ri-shield-check-line" variant="success" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Chi tiết khoản chi
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Lệnh công việc (WO)</span>
                    <span className="text-sm font-bold text-os-primary font-mono">{item.work_order?.order_number || 'Báo cáo độc lập'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người ghi nhận</span>
                    <span className="text-sm font-bold text-slate-700">{item.recorder?.full_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Đơn giá</span>
                    <span className="text-sm font-bold text-slate-700">{formatCurrency(item.unit_price)}</span>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold text-slate-400  mb-2">Mô tả mục chi</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 leading-relaxed">
                    {item.description || 'Không có mô tả chi tiết cho khoản chi này.'}
                  </p>
                </div>
              </Card>

              {item.status_code === 'approved' && (
                <Card className="p-6 border-emerald-100 bg-emerald-50/30">
                  <h3 className="font-bold text-emerald-900 mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-emerald-600" />
                    Xác nhận phê duyệt
                  </h3>
                  <div className="flex items-center gap-4">
                    <Avatar src={item.approver?.avatar_url} name={item.approver?.full_name} size="md" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">Phê duyệt bởi: {item.approver?.full_name}</p>
                      <p className="text-[10px] text-slate-500  font-bold tracking-wider">
                        Ngày: {item.approved_at ? new Date(item.approved_at).toLocaleString('vi-VN') : '---'}
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {(activeTab === 'work' || activeTab === 'media' || activeTab === 'history') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Dữ liệu tài chính đang được tổng hợp từ chứng từ và nhật ký công việc."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-refund-2-line text-os-primary" />
              Tóm tắt tài chính
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400  font-bold tracking-wider">Trạng thái thanh toán</span>
                <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                  {statusLabels[item.status_code]}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Phương thức</span>
                <span className="font-bold  tracking-tighter">Chuyển khoản / Định mức</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <div className="flex justify-between items-end mb-1">
                  <span className="text-[10px] text-slate-400  font-bold">Tổng cộng</span>
                  <span className="text-xl font-bold text-os-primary">{formatCurrency(item.total_amount)}</span>
                </div>
                <p className="text-[9px] text-slate-500 italic">Giá đã bao gồm VAT và các loại phí phát sinh hiện trường.</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-shield-user-line text-slate-400" />
              Chứng từ liên quan
            </h4>
            <div className="py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <i className="ri-file-list-3-line text-3xl text-slate-300 mb-2 block" />
              <p className="text-[11px] text-slate-400 px-4 text-center">Chưa có tệp đính kèm hóa đơn hoặc chứng từ hiện trường.</p>
              <Button variant="ghost" size="sm" className="mt-3 text-os-primary">Tải lên ngay</Button>
            </div>
          </Card>
        </div>
      </div>

      <CostFormModal
        isOpen={showEdit}
        item={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
